import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { components } from "api/generated";
import {
  getPlatformUserMembershipsByPlatformUserMembershipIdChangeOptions,
  postPlatformUserMembershipsByPlatformUserMembershipIdChangeMembership,
} from "api/methods";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Check from "atoms/icon/icons/ic_check_small_ol.svg";
import Lock from "atoms/icon/icons/lock.svg";
import { Notification, useNotify } from "atoms/notification";
import { TextButton } from "atoms/text-button/TextButton";
import TopNav from "molecules/navbars/TopNav";
import { useTranslation } from "@/core/i18n";
import { queryClient } from "pages/Root";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { formatBillingPeriodInterval, parseBillingPeriodName } from "utils/billingPeriod.helpers";

import { invalidateMembershipQueries } from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const changeOptions = queryClient.fetchQuery({
    ...getPlatformUserMembershipsByPlatformUserMembershipIdChangeOptions.keyed({
      platformUserMembershipId,
    }),
  });

  return waitObj({ platformUserMembershipId, changeOptions });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipChangeLoader: LoaderFunction = async ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  const data = await load(platformUserMembershipId);

  // A change is already scheduled — the manage card owns that state.
  if (data.changeOptions.pendingChange) {
    return redirect("/wallet/memberships/manage");
  }

  return data;
};

type ChangeOption = components["schemas"]["DisplayMembershipChangeOption"];

export const WalletMembershipChangePage = () => {
  const { platformUserMembershipId, changeOptions } = useLoaderData() as LoaderData;
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [notificationProps, notify] = useNotify();

  const [selectedMembershipId, setSelectedMembershipId] = useState<string>();
  const [step, setStep] = useState<"select" | "confirm">("select");

  const { currentMembership, options } = changeOptions;
  const selectedOption = options.find(({ membershipId }) => membershipId === selectedMembershipId);
  const renewalDate = dayjs(currentMembership.nextRenewalAt);

  const changeMembershipMutation = useMutation({
    mutationFn: (targetMembershipId: string) =>
      postPlatformUserMembershipsByPlatformUserMembershipIdChangeMembership({
        platformUserMembershipId,
        targetMembershipId,
      }),
    onSuccess: async () => {
      await invalidateMembershipQueries(queryClient);

      navigate("/wallet/memberships/manage");
    },
    onError: () => {
      notify.error(t("walletMembershipChangePage.genericError"), {
        timeout: 5000,
        dismissable: false,
      });
    },
  });

  const formatOptionPrice = (option: {
    billingPeriodCost: number;
    billingPeriodInterval: number;
    billingPeriodIntervalCount: number;
  }) =>
    formatBillingPeriodInterval(
      option.billingPeriodCost,
      parseBillingPeriodName(option.billingPeriodInterval),
      option.billingPeriodIntervalCount,
    );

  const optionRow = (option: ChangeOption) => {
    const isSelected = option.membershipId === selectedMembershipId;

    return (
      <button
        key={option.membershipId}
        role="radio"
        aria-checked={isSelected}
        aria-label={`${option.name}, ${formatOptionPrice(option)}`}
        className={`flex w-full flex-col gap-y-1 rounded-lg border p-4 text-left ${
          !option.eligible
            ? "border-light-grey opacity-60"
            : isSelected
              ? "border-brand-color bg-brand-color/10"
              : "border-light-grey"
        }`}
        disabled={!option.eligible}
        type="button"
        onClick={() => setSelectedMembershipId(option.membershipId)}
      >
        <div className="flex w-full items-center gap-x-2">
          <span className="grow truncate text-bold1">{option.name}</span>
          <span className="shrink-0 rounded-full bg-one px-3 py-1 text-body2">
            {t(
              option.direction === "Downgrade"
                ? "walletMembershipChangePage.directionDowngrade"
                : "walletMembershipChangePage.directionUpgrade",
            )}
          </span>
          {!option.eligible && <Icon svg={Lock} size="xsmall" />}
          {isSelected && <Icon svg={Check} size="xsmall" color="brandColor" />}
        </div>
        <span className="text-body2 text-secondary">{formatOptionPrice(option)}</span>
        {!option.eligible && option.blockedUntil && (
          <span className="text-body2 text-secondary">
            {t("walletMembershipChangePage.availableAfter", {
              date: dayjs(option.blockedUntil).format("MMM D, YYYY"),
            })}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="relative flex h-full flex-col bg-white">
      <Notification {...notificationProps} />
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => (step === "confirm" ? setStep("select") : navigate(-1))}
      >
        <div className="flex justify-center text-sub2">{t("walletMembershipChangePage.title")}</div>
      </TopNav>

      {step === "select" && (
        <div className="flex grow flex-col gap-y-6 px-6 pb-8 pt-3">
          <div className="flex flex-col gap-y-1">
            <div className="text-title">{t("walletMembershipChangePage.chooseTitle")}</div>
            <div className="text-body2 text-secondary">
              {t("walletMembershipChangePage.currentPlan", {
                planName: currentMembership.name,
                price: formatOptionPrice(currentMembership),
              })}
            </div>
          </div>
          {options.length === 0 ? (
            <div className="text-body2 text-secondary">
              {t("walletMembershipChangePage.noOptions")}
            </div>
          ) : (
            <div
              role="radiogroup"
              aria-label={t("walletMembershipChangePage.chooseTitle")}
              className="flex flex-col gap-y-3"
            >
              {options.map(optionRow)}
            </div>
          )}
          <div className="grow" />
          <Button fullWidth disabled={!selectedOption} onClick={() => setStep("confirm")}>
            {t("common.continue")}
          </Button>
        </div>
      )}

      {step === "confirm" && selectedOption && (
        <div className="flex grow flex-col gap-y-6 px-6 pb-8 pt-3">
          <div className="text-title">{t("walletMembershipChangePage.confirmTitle")}</div>
          <div className="flex flex-col gap-y-3 rounded-lg border border-light-grey p-4">
            <div className="flex justify-between">
              <span>{currentMembership.name}</span>
              <span className="text-bold1">
                {t("walletMembershipChangePage.toPlan", { planName: selectedOption.name })}
              </span>
            </div>
            <div className="flex justify-between text-body2">
              <span className="text-secondary">{t("walletMembershipChangePage.newPrice")}</span>
              <span>{formatOptionPrice(selectedOption)}</span>
            </div>
            <div className="flex justify-between text-body2">
              <span className="text-secondary">{t("walletMembershipChangePage.effective")}</span>
              <span>{renewalDate.format("MMM D, YYYY")}</span>
            </div>
            <div className="flex justify-between text-body2">
              <span className="text-secondary">{t("walletMembershipChangePage.dueToday")}</span>
              <span>{formatCurrency(0)}</span>
            </div>
          </div>
          <div className="text-body2 text-secondary">
            {t("walletMembershipChangePage.disclaimer")}
          </div>
          <div className="grow" />
          <Button
            fullWidth
            disabled={changeMembershipMutation.isLoading}
            onClick={() => changeMembershipMutation.mutate(selectedOption.membershipId)}
          >
            {t("walletMembershipChangePage.confirmChange")}
          </Button>
          <div className="flex justify-center">
            <TextButton
              disabled={changeMembershipMutation.isLoading}
              onClick={() => navigate("/wallet/memberships/manage")}
            >
              {t("walletMembershipChangePage.keepCurrent", { planName: currentMembership.name })}
            </TextButton>
          </div>
        </div>
      )}
    </div>
  );
};
